'use client'

import { createContext, useContext, useEffect, useMemo } from 'react'
import { useRequireAuth } from '~/hooks/useRequireAuth'
import type { User } from '@auth0/nextjs-auth0/types'
import { useBaseEvaluation } from '~/components/Evaluation/EvaluationDocumentProvider'
import {
  EvaluationCollaborator,
  useBaseEvaluationCollaborators,
} from '~/components/Evaluation/useEvaluationCollaborators'

type EvaluationUserContextValue = {
  user?: User | null
  isViewer?: boolean
  collaborator?: EvaluationCollaborator
}

export const EvaluationUserContext = createContext<EvaluationUserContextValue>(
  {}
)

export const useEvaluationUserContext = () => useContext(EvaluationUserContext)

export const EvaluationUserContextProvider = ({ children }) => {
  const { user, isLoading } = useRequireAuth()
  const { evaluation } = useBaseEvaluation()
  const {
    collaborator,
    refetchCollaborators,
    refetchWorkspaceMembers,
    workspaceId,
    evaluationId,
  } = useBaseEvaluationCollaborators()

  useEffect(() => {
    if (evaluationId) {
      refetchCollaborators()
    }
  }, [evaluationId])

  useEffect(() => {
    if (workspaceId) {
      refetchWorkspaceMembers()
    }
  }, [workspaceId])

  const contextValue = useMemo(() => {
    const isEditor =
      collaborator?.roles?.includes('Admin') ||
      collaborator?.roles?.includes('Editor') ||
      collaborator?.roles?.includes('Owner')
    const isViewer = !isEditor // if not data yet, assume a viewer just to be safe
    return {
      user,
      isViewer,
      collaborator,
    }
  }, [user, collaborator, isLoading, evaluation])

  return (
    <EvaluationUserContext.Provider value={contextValue}>
      {children}
    </EvaluationUserContext.Provider>
  )
}
